import Vue from "vue";
import axios from "axios";
import router from "@/router";
import store from "./store";

const AUTH_ENDPOINT = "/auth/authenticate";
const USER_LIST_ENDPOINT = "/user/search-by-name";
const USER_LIST_SINGLE_ENDPOINT = "/user";

class CheckAPI {
  constructor() {
    this.createInstance();
  }

  createInstance = () => {
    this.instance = axios.create({
      baseURL: process.env.VUE_APP_API_ROOT,
      headers: {
        Authorization: this.token
          ? this.token
          : `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      withCredentials: false,
    });
  };

  setToken = (token) => {
    localStorage.setItem("token", token);
    this.token = `Bearer ${token}`;
    this.createInstance();
  };

  authenticate = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_ROOT}${AUTH_ENDPOINT}`,
        data,
        {
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );
      this.setToken(response.data.data.token);
      return response;
    } catch (e) {
      this.handleError(e);
      return e.response;
    }
  };

  getUserById = async (id) => {
    try {
      return await this.instance.get(`${USER_LIST_SINGLE_ENDPOINT}`, {
        params: {
          userId: id,
        },
      });
    } catch (e) {
      this.handleError(e.response.data.message, true);
    }
  };

  getUsers = async (params) => {
    try {
      return await this.instance.post(USER_LIST_ENDPOINT, params);
    } catch (e) {
      this.handleError(e);
    }
  };

  setUserInfoById = async (data) => {
    try {
      const response = await this.instance.put(
        `${USER_LIST_SINGLE_ENDPOINT}`,
        data
      );
      this.handleSuccessMessage(true, "/individuals");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  createNewUser = async (data) => {
    try {
      const response = await this.instance.post(
        `${USER_LIST_SINGLE_ENDPOINT}`,
        data
      );
      this.handleSuccessMessage(true, "/?page=1");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteUserById = async (id) => {
    try {
      const response = await this.instance.delete(
        `${USER_LIST_SINGLE_ENDPOINT}`,
        {
          params: {
            userId: id,
          },
        }
      );
      this.handleSuccessMessage(true, "/", "User successfully deleted");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getCourseTemplates = async (page, size) => {
    try {
      const response = await this.instance.post("/course-template/search", {
        page,
        size,
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getLessonTemplates = async (page, size, courseTemplateId) => {
    try {
      const response = await this.instance.post("/lesson-template/search", {
        page,
        size,
        courseTemplateId: parseInt(courseTemplateId),
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getUnitTemplates = async (page, size, lessonTemplateId) => {
    try {
      const response = await this.instance.post("/unit-template/search", {
        page,
        size,
        lessonTemplateId: parseInt(lessonTemplateId),
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getElementTemplates = async (page, size, unitTemplateId) => {
    try {
      const response = await this.instance.post("/element-template/search", {
        page,
        size,
        unitTemplateId: parseInt(unitTemplateId),
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getCriteriaTemplates = async (page, size, elementTemplateId) => {
    try {
      const response = await this.instance.post("/criteria-template/search", {
        page,
        size,
        elementTemplateId: parseInt(elementTemplateId),
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  createCourseTemplate = async (data) => {
    try {
      const response = await this.instance.post("/course-template", data);
      this.handleSuccessMessage(
        true,
        "/templates?page=1 ",
        "Course successfully created"
      );
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  createLessonTemplate = async (data) => {
    try {
      const response = await this.instance.post("/lesson-template", data);
      this.handleSuccessMessage(false, "", "Lesson successfully created");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  createUnitTemplate = async (data) => {
    try {
      const response = await this.instance.post("/unit-template", data);
      this.handleSuccessMessage(false, "", "Unit successfully created");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  createElementTemplate = async (data) => {
    try {
      const response = await this.instance.post("/element-template", data);
      this.handleSuccessMessage(false, "", "Element successfully created");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  createCriteriaTemplate = async (data) => {
    try {
      const response = await this.instance.post("/criteria-template", data);
      this.handleSuccessMessage(false, "", "Criteria successfully created");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getCourseTemplateById = async (courseTemplateId) => {
    try {
      const response = await this.instance.get("/course-template", {
        params: {
          courseTemplateId,
        },
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateCourseTemplateById = async (data) => {
    try {
      const response = await this.instance.put("/course-template", data);
      this.handleSuccessMessage(false, "", "Course successfully updated");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getLessonTemplateById = async (lessonTemplateId) => {
    try {
      const response = await this.instance.get("/lesson-template", {
        params: {
          lessonTemplateId,
        },
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateLessonTemplateById = async (data) => {
    try {
      const response = await this.instance.put("/lesson-template", data);
      this.handleSuccessMessage(false, "", "Lesson successfully updated");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getUnitTemplateById = async (unitTemplateId) => {
    try {
      const response = await this.instance.get("/unit-template", {
        params: {
          unitTemplateId,
        },
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateUnitTemplateById = async (data) => {
    try {
      const response = await this.instance.put("/unit-template", data);
      this.handleSuccessMessage(false, "", "Unit successfully updated");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getElementTemplateById = async (elementTemplateId) => {
    try {
      const response = await this.instance.get("/element-template", {
        params: {
          elementTemplateId,
        },
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateElementTemplateById = async (data) => {
    try {
      const response = await this.instance.put("/element-template", data);
      this.handleSuccessMessage(false, "", "Element successfully updated");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getCriteriaTemplateById = async (criteriaTemplateId) => {
    try {
      const response = await this.instance.get("/criteria-template", {
        params: {
          criteriaTemplateId,
        },
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateCriteriaTemplateById = async (data) => {
    try {
      const response = await this.instance.put("/criteria-template", data);
      this.handleSuccessMessage(false, "", "Criteria successfully updated");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getCourses = async (page, size) => {
    try {
      const response = await this.instance.post("/course/search", {
        page,
        size,
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getAssignableCourses = async (page, size) => {
    try {
      const response = await this.instance.post(
        "/course-template/search-assignable",
        {
          page,
          size,
        }
      );
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  getAssignableUsers = async (emailSubstring) => {
    try {
      const response = await this.instance.post("/user/search", {
        page: 0,
        size: 30,
        emailSubstring,
      });
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  handleCourseDelete = async (courseId) => {
    try {
      await this.instance.delete("/course", {
        params: {
          courseId,
        },
      });
      this.handleSuccessMessage(false, "", "Successfully deleted");
    } catch (e) {
      this.handleError(e);
    }
  };

  handleCourseAssign = async (data) => {
    try {
      const response = await this.instance.post("/course/assign", data);
      this.handleSuccessMessage(
        false,
        "",
        "Student successfully added to the course"
      );
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  handleCourseTemplateDelete = async (courseTemplateId) => {
    try {
      const response = await this.instance.delete("course-template", {
        params: {
          courseTemplateId,
        },
      });
      this.handleSuccessMessage(
        false,
        "",
        "Course Template successfully deleted"
      );
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  handleLessonTemplateDelete = async (lessonTemplateId) => {
    try {
      const response = await this.instance.delete("lesson-template", {
        params: {
          lessonTemplateId,
        },
      });
      this.handleSuccessMessage(false, "", "Lesson successfully deleted");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  handleUnitTemplateDelete = async (unitTemplateId) => {
    try {
      const response = await this.instance.delete("unit-template", {
        params: {
          unitTemplateId,
        },
      });
      this.handleSuccessMessage(false, "", "Unit successfully deleted");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  handleElementTemplateDelete = async (elementTemplateId) => {
    try {
      const response = await this.instance.delete("element-template", {
        params: {
          elementTemplateId,
        },
      });
      this.handleSuccessMessage(false, "", "Element successfully deleted");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  handleCriteriaTemplateDelete = async (criteriaTemplateId) => {
    try {
      const response = await this.instance.delete("criteria-template", {
        params: {
          criteriaTemplateId,
        },
      });
      this.handleSuccessMessage(false, "", "Criteria successfully deleted");
      return response;
    } catch (e) {
      this.handleError(e);
    }
  };

  dataPair = {
    lessonTemplateId: "lessonId",
    courseTemplateId: "courseId",
    criteriaTemplateId: "criteriaId",
    elementTemplateId: "elementId",
    unitTemplateId: "unitId",
  };

  async getBC(params) {
    try {
      const transformedData = Object.entries(params).reduce((acc, single) => {
        const [key, value] = single;
        acc[this.dataPair[key]] = parseInt(value);
        return acc;
      }, {});
      return await this.instance.post("/breadcrumb", transformedData);
    } catch (e) {
      this.handleError(e);
    }
  }

  async handleResetPassword(data) {
    try {
      return await this.instance.post("/user/reset-password", {
        newPassword: data.password,
        resetPasswordString: data.resetPasswordString,
      });
    } catch (e) {
      this.handleError(e);
      return e.response;
    }
  }

  handleError = (e, redirect) => {
    if (e.response && e.response.status && 403 === e.response.status) {
      store.dispatch("logoutUser");
    }
    Vue.notify({
      group: "error",
      type: "error",
      title: "Error occurred",
      text: e.response.data.data.message
        ? e.response.data.data.message
        : "Error occurred, please try again later",
    });
    redirect && router.push({ path: "/" });
  };

  handleSuccessMessage = (
    redirect,
    redirectURL = "/",
    e = "Successfully updated"
  ) => {
    Vue.notify({
      group: "error",
      type: "success",
      text: e,
    });
    redirect && router.push({ path: redirectURL });
  };
}

const CheckAPInstance = new CheckAPI();

export default CheckAPInstance;
